import { PromptMediaFit } from "@src/types.generated";
import { LinkType } from "@src/lib/utils/files";
import { DEFAULT_PHONE_HEIGHT, DEFAULT_PHONE_WIDTH } from "./PhoneOutline";

/**
 * Author-managed media layout for a course screen.
 *
 * A prompt is either legacy (every field null — each client keeps its own
 * historical sizing) or managed (every field set — the builder, the web player
 * and the phone render identically). There is deliberately no in-between:
 * scadmin rejects a half-set layout, because that is exactly where the
 * platforms would silently disagree.
 *
 * Zoom is the on-screen crop: how far past "cover" a Fill image is scaled,
 * around its focal point. The uploaded media is never changed, so a crop is
 * always undoable — Reset returns the whole original.
 */
export type PromptMediaLayout = {
  heightPercent: number | null;
  fit: PromptMediaFit | null;
  focalX: number | null;
  focalY: number | null;
  zoom: number | null;
};

export type ResolvedPromptMediaLayout = {
  heightPercent: number;
  fit: PromptMediaFit;
  focalX: number;
  focalY: number;
  zoom: number;
};

// Mirrored in scadmin's handle_set_prompt_media_layout_action; keep in sync.
export const MIN_MEDIA_HEIGHT_PERCENT = 25;
export const MAX_MEDIA_HEIGHT_PERCENT = 80;
export const DEFAULT_MEDIA_HEIGHT_PERCENT = 50;
export const DEFAULT_MEDIA_FOCAL = 0.5;
export const MIN_MEDIA_ZOOM = 1;
export const MAX_MEDIA_ZOOM = 4;

/** Every field null: the screen keeps its historical sizing. */
export const LEGACY_MEDIA_LAYOUT: PromptMediaLayout = {
  heightPercent: null,
  fit: null,
  focalX: null,
  focalY: null,
  zoom: null,
};

export type PromptWithMediaLayout = {
  mediaHeightPercent?: number | null;
  mediaFit?: PromptMediaFit | null;
  mediaFocalX?: number | null;
  mediaFocalY?: number | null;
  mediaZoom?: number | null;
};

export const readMediaLayout = (
  prompt: PromptWithMediaLayout,
): PromptMediaLayout => ({
  heightPercent: prompt.mediaHeightPercent ?? null,
  fit: prompt.mediaFit ?? null,
  focalX: prompt.mediaFocalX ?? null,
  focalY: prompt.mediaFocalY ?? null,
  zoom: prompt.mediaZoom ?? null,
});

export const writeMediaLayout = (
  prompt: PromptWithMediaLayout,
  layout: PromptMediaLayout,
): void => {
  prompt.mediaHeightPercent = layout.heightPercent;
  prompt.mediaFit = layout.fit;
  prompt.mediaFocalX = layout.focalX;
  prompt.mediaFocalY = layout.focalY;
  prompt.mediaZoom = layout.zoom;
};

export const isManagedLayout = (layout: PromptMediaLayout): boolean =>
  layout.heightPercent !== null && layout.fit !== null;

export const isSameLayout = (
  a: PromptMediaLayout,
  b: PromptMediaLayout,
): boolean =>
  a.heightPercent === b.heightPercent &&
  a.fit === b.fit &&
  a.focalX === b.focalX &&
  a.focalY === b.focalY &&
  (a.zoom ?? (a.heightPercent === null ? null : MIN_MEDIA_ZOOM)) ===
    (b.zoom ?? (b.heightPercent === null ? null : MIN_MEDIA_ZOOM));

/**
 * Fills the layout in so callers never branch on legacy vs managed.
 *
 * A legacy screen resolves to Fit: opting in at Fit preserves what the author
 * already sees, so the first height drag never silently starts cropping.
 */
export const resolveLayout = (
  layout: PromptMediaLayout,
): ResolvedPromptMediaLayout => ({
  heightPercent: layout.heightPercent ?? DEFAULT_MEDIA_HEIGHT_PERCENT,
  fit: layout.fit ?? PromptMediaFit.Fit,
  focalX: layout.focalX ?? DEFAULT_MEDIA_FOCAL,
  focalY: layout.focalY ?? DEFAULT_MEDIA_FOCAL,
  zoom: layout.zoom ?? MIN_MEDIA_ZOOM,
});

/**
 * Media whose box height the author can set. Mirrors the branch
 * `usePromptMediaCSS` uses for its 80% cap — keep the two together, since a
 * drift would let an author resize media the cap does not protect.
 */
export const isResizableMediaType = (
  linkType: LinkType,
  isVideoOrMuxVideo: boolean,
): boolean =>
  [LinkType.IMAGE, LinkType.GIF].includes(linkType) || isVideoOrMuxVideo;

/**
 * Media whose hover menu floats inside it, over the picture. A video keeps
 * the menu off its bottom edge, where the player draws its own controls.
 */
export const isMenuInsideMediaType = (
  linkType: LinkType,
  isVideoOrMuxVideo: boolean,
): boolean =>
  [LinkType.IMAGE, LinkType.GIF].includes(linkType) && !isVideoOrMuxVideo;

/**
 * Where the builder's hover menu sits on a screen's media:
 * - `inside`: over an image or GIF, inside its bottom edge;
 * - `below`: under other resizable media (video), clear of both the height
 *   grip on its bottom edge and the player's controls;
 * - `straddle`: across the bottom edge of anything else (links, PDFs).
 */
export type BubbleMenuPlacement = "inside" | "below" | "straddle";

export const bubbleMenuPlacement = (
  linkType: LinkType,
  isVideoOrMuxVideo: boolean,
  canEditLayout: boolean,
): BubbleMenuPlacement => {
  if (!canEditLayout || !isResizableMediaType(linkType, isVideoOrMuxVideo))
    return "straddle";
  return isMenuInsideMediaType(linkType, isVideoOrMuxVideo)
    ? "inside"
    : "below";
};

/**
 * Media the author can crop on screen (pan and zoom). Narrower than the
 * resizable set — there is no object-position or scale equivalent for a video
 * player's surface. Giphy GIFs are out too: the app plays them as MP4 video
 * (mobile `linkIsGiphy` → `getGiphyMp4`), so a crop would not reach learners.
 */
export const isCroppableMediaType = (
  linkType: LinkType,
  url: string,
): boolean =>
  [LinkType.IMAGE, LinkType.GIF].includes(linkType) &&
  !url.toLowerCase().includes("giphy.com/media");

export const clampHeightPercent = (value: number): number =>
  Math.min(
    MAX_MEDIA_HEIGHT_PERCENT,
    Math.max(MIN_MEDIA_HEIGHT_PERCENT, Math.round(value)),
  );

/** Clamped to [0, 1] and rounded to 3 places so a pan is one clean value. */
export const clampFocal = (value: number): number =>
  Math.round(Math.min(1, Math.max(0, value)) * 1000) / 1000;

/** CSS `object-position` for a focal point. */
export const focalToObjectPosition = (focalX: number, focalY: number): string =>
  `${focalX * 100}% ${focalY * 100}%`;

export const clampZoom = (value: number): number =>
  Math.round(Math.min(MAX_MEDIA_ZOOM, Math.max(MIN_MEDIA_ZOOM, value)) * 100) /
  100;

/**
 * The crop as CSS for an `object-fit: cover` image filling its box: position
 * at the focal point, then scale around that same point. The web player,
 * the builder and the app (transformOrigin + scale) all render it this way.
 */
export const fillCropStyle = (layout: {
  focalX: number;
  focalY: number;
  zoom: number;
}): {
  objectPosition: string;
  transform?: string;
  transformOrigin?: string;
} => {
  const position = focalToObjectPosition(layout.focalX, layout.focalY);
  return layout.zoom > MIN_MEDIA_ZOOM
    ? {
        objectPosition: position,
        transform: `scale(${layout.zoom})`,
        transformOrigin: position,
      }
    : { objectPosition: position };
};

/**
 * Where the whole image sits relative to its box for a crop, in box pixels.
 * Cover-scaled then zoomed, and placed so the image's focal point lines up
 * with the box's: left = fx·(W − Iw), top = fy·(H − Ih). Crop mode draws the
 * ghost of the hidden parts with this.
 */
export const cropImageRect = (args: {
  naturalWidth: number;
  naturalHeight: number;
  boxWidth: number;
  boxHeight: number;
  focalX: number;
  focalY: number;
  zoom: number;
}): { left: number; top: number; width: number; height: number } | null => {
  const { naturalWidth, naturalHeight, boxWidth, boxHeight } = args;
  if (!naturalWidth || !naturalHeight || !boxWidth || !boxHeight) return null;
  const coverScale = Math.max(
    boxWidth / naturalWidth,
    boxHeight / naturalHeight,
  );
  const width = naturalWidth * coverScale * args.zoom;
  const height = naturalHeight * coverScale * args.zoom;
  return {
    left: args.focalX * (boxWidth - width),
    top: args.focalY * (boxHeight - height),
    width,
    height,
  };
};

/**
 * Zoom from dragging an image corner: the image scales around the focal
 * point, so zoom follows the pointer's distance from it.
 */
export const zoomFromCornerDrag = (args: {
  startZoom: number;
  originX: number;
  originY: number;
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
}): number => {
  const startDistance = Math.hypot(
    args.startX - args.originX,
    args.startY - args.originY,
  );
  if (!startDistance) return clampZoom(args.startZoom);
  const distance = Math.hypot(
    args.currentX - args.originX,
    args.currentY - args.originY,
  );
  return clampZoom((args.startZoom * distance) / startDistance);
};

/**
 * The builder's media box inside the phone: inset 6px on each side (mx-1.5),
 * and as tall as the author's share of the phone's height. The builder sizes
 * the box in these phone pixels; the web player and the app keep this ratio,
 * so every surface crops exactly what the author saw.
 */
export const MEDIA_BOX_INSET_X = 12;
export const mediaBoxHeightPx = (heightPercent: number): number =>
  (DEFAULT_PHONE_HEIGHT * heightPercent) / 100;
export const mediaBoxAspectRatio = (heightPercent: number): number =>
  (DEFAULT_PHONE_WIDTH - MEDIA_BOX_INSET_X) / mediaBoxHeightPx(heightPercent);

/**
 * Converts a pointer drag into a new height percentage.
 *
 * `scale` is the CSS transform the phone is rendered at; the builder scales the
 * phone down to fit its stage, so raw pointer pixels move the edge further than
 * they appear to. Dividing by it keeps the handle under the cursor.
 */
export const heightPercentFromDrag = (args: {
  startPercent: number;
  startY: number;
  currentY: number;
  scale: number;
  phoneHeight?: number;
}): number => {
  const phoneHeight = args.phoneHeight ?? DEFAULT_PHONE_HEIGHT;
  if (!args.scale || !phoneHeight) return clampHeightPercent(args.startPercent);
  const deltaPercent =
    ((args.currentY - args.startY) / args.scale / phoneHeight) * 100;
  return clampHeightPercent(args.startPercent + deltaPercent);
};

/**
 * Converts a pointer drag into a new focal point.
 *
 * In `cover` the rendered image overflows its box on at most one axis. Panning
 * moves the image, so the focal point moves against the drag. An axis with no
 * overflow cannot pan and keeps its current value.
 */
export const focalFromDrag = (args: {
  startFocalX: number;
  startFocalY: number;
  deltaX: number;
  deltaY: number;
  scale: number;
  overflowX: number;
  overflowY: number;
}): { focalX: number; focalY: number } => {
  const scale = args.scale || 1;
  const focalX =
    args.overflowX > 0
      ? clampFocal(args.startFocalX - args.deltaX / scale / args.overflowX)
      : args.startFocalX;
  const focalY =
    args.overflowY > 0
      ? clampFocal(args.startFocalY - args.deltaY / scale / args.overflowY)
      : args.startFocalY;
  return { focalX, focalY };
};

/**
 * How far a `cover` image (times `zoom`) overflows its box on each axis, in
 * box pixels. Returns zeroes until the natural dimensions are known.
 */
export const coverOverflow = (args: {
  naturalWidth: number;
  naturalHeight: number;
  boxWidth: number;
  boxHeight: number;
  zoom?: number;
}): { overflowX: number; overflowY: number } => {
  const { naturalWidth, naturalHeight, boxWidth, boxHeight } = args;
  if (!naturalWidth || !naturalHeight || !boxWidth || !boxHeight) {
    return { overflowX: 0, overflowY: 0 };
  }
  const coverScale =
    Math.max(boxWidth / naturalWidth, boxHeight / naturalHeight) *
    (args.zoom ?? 1);
  return {
    overflowX: Math.max(0, naturalWidth * coverScale - boxWidth),
    overflowY: Math.max(0, naturalHeight * coverScale - boxHeight),
  };
};
